import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import MarkChatReadRoundedIcon from "@mui/icons-material/MarkChatReadRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { Link as RouterLink } from "react-router";
import { useSelector } from "react-redux";
import { useMemo } from "react";

import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import {
  DashboardCardEyebrow,
  DashboardCardHeader,
  DashboardCardState,
  DashboardPanelCard,
  DashboardRowLink,
  DashboardRowList,
  DashboardRowMarker,
  DashboardRowText,
} from "_shared/_views/dashboards/dashboard-card";
import { useGetForumsQuery } from "messaging/_api/messaging-api";
import ForumQuery, {
  ForumFilterParameters,
  ForumOrderField,
  ForumOrderParameters,
} from "messaging/forums/_api/queries/forum-query";
import Forum from "messaging/forums/forum";
import type { ForumJSON } from "messaging/forums/_schemas/forum-schema";
import { getForumChatSubtitle, getForumChatTitle } from "messaging/forums/forum-chat-display";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { useSession } from "users/__session/session-context";

const RowCount = styled(Typography)({
  fontWeight: 700,
  lineHeight: 1,
});

export const communicationsRoute = "/communications";
export const dashboardUnreadConversationLimit = 3;

/**
 * How many conversations are read before the unread ones are picked out of them.
 *
 * `ForumFilterParameters` has no unread filter, so this card asks for a page and counts what comes
 * back, the way `WaitingOnYouCard` does with network invitations. A user past this many
 * conversations sees a header total that reads low rather than one that reads wrong, and the row
 * into `/communications` still reaches everything.
 */
export const conversationPageSize = 100;

export function forumRoute(forumId: string): string {
  return `${communicationsRoute}/forums/${forumId}`;
}

/**
 * The conversations with something unread in them, most unread first.
 *
 * `ForumOrderField` offers `CreatedDate`, `Name`, `EntityId`, `EntityClass` and `Context` — nothing
 * about activity — so "most recently active" is not a sort the server can do and the ordering is
 * done here instead. Unread count is the honest proxy: it is the number the row already shows, so
 * the order and the markers agree with each other.
 */
export function unreadConversations(forums: Forum[]): Forum[] {
  return forums
    .filter((forum) => Boolean(forum.id) && (forum.numUnreadMessages ?? 0) > 0)
    .sort((left, right) => (right.numUnreadMessages ?? 0) - (left.numUnreadMessages ?? 0));
}

export function totalUnreadMessages(forums: Forum[]): number {
  return forums.reduce((total, forum) => total + (forum.numUnreadMessages ?? 0), 0);
}

const unreadConversationsQuery = new ForumQuery({
  filterParameters: new ForumFilterParameters({}),
  orderParameters: new ForumOrderParameters({
    orderBy: ForumOrderField.CreatedDate,
    orderDir: OrderDirection.Descending,
  }),
  paginationParameters: PaginationParameters.create({ pageIndex: 0, pageSize: conversationPageSize }),
});

/**
 * Who has spoken to you and not been answered.
 *
 * The forums endpoint is already scoped to the calling user and counts each forum's unread
 * messages for them, so there is no "mine" filter to pass and no count to compute here — only the
 * choice of which few to show. This card owns conversations on the dashboard: the recent-activity
 * card excludes message notifications precisely so one unread message is not counted twice on one
 * screen (#5077).
 */
export default function UnreadConversationsCard() {
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  // Communications is permission-gated at the route, so a user without it must not be shown rows
  // leading into it.
  const includesCommunications = sessionPermissions?.includesAccessToCommunications === true;

  const forumsQuery = useGetForumsQuery({ query: unreadConversationsQuery }, { skip: !includesCommunications });

  const unread = useMemo(
    () => unreadConversations((forumsQuery.data ?? []).map((json: ForumJSON) => Forum.fromJSON(json))),
    [forumsQuery.data],
  );
  const visible = unread.slice(0, dashboardUnreadConversationLimit);
  const additionalConversationCount = Math.max(0, unread.length - dashboardUnreadConversationLimit);
  const unreadTotal = totalUnreadMessages(unread);

  if (!includesCommunications) return null;

  return (
    <DashboardPanelCard>
      <DashboardCardHeader>
        <Box>
          <DashboardCardEyebrow>
            <ForumRoundedIcon color="primary" fontSize="small" />
            <Typography color="primary" variant="overline">Conversations</Typography>
          </DashboardCardEyebrow>
          <Typography variant="h5" component="h2">
            {unreadTotal > 0 ? `${unreadTotal} unread ${unreadTotal === 1 ? "message" : "messages"}` : "Unread messages"}
          </Typography>
        </Box>
        <Button component={RouterLink} to={communicationsRoute} size="small">View all</Button>
      </DashboardCardHeader>
      {forumsQuery.isLoading && (
        <DashboardCardState role="status">
          <CircularProgress size={20} />
          <Typography variant="body2">Loading your conversations…</Typography>
        </DashboardCardState>
      )}
      {!forumsQuery.isLoading && forumsQuery.isError && (
        <DashboardCardState>
          <Typography color="error" variant="body2">Your conversations could not be loaded.</Typography>
        </DashboardCardState>
      )}
      {!forumsQuery.isLoading && !forumsQuery.isError && visible.length === 0 && (
        <DashboardCardState>
          <MarkChatReadRoundedIcon color="success" fontSize="small" />
          <Typography variant="body2">You are caught up.</Typography>
        </DashboardCardState>
      )}
      {!forumsQuery.isLoading && !forumsQuery.isError && visible.length > 0 && (
        <DashboardRowList>
          <Stack spacing={1}>
            {visible.map((forum) => {
              const title = getForumChatTitle(forum, session.user?.fullName);
              const unreadCount = forum.numUnreadMessages ?? 0;
              return (
                <DashboardRowLink
                  key={forum.id?.value}
                  to={forumRoute(String(forum.id?.value))}
                  aria-label={`${unreadCount} unread in ${title}`}
                >
                  {/* Loud on purpose: somebody has spoken to this user and is waiting to be
                      answered, which is the same kind of thing the queue card holds. */}
                  <DashboardRowMarker aria-hidden="true">
                    <RowCount variant="h6">{unreadCount}</RowCount>
                  </DashboardRowMarker>
                  <DashboardRowText>
                    <Typography noWrap variant="subtitle2">{title}</Typography>
                    <Typography color="text.secondary" noWrap variant="caption">
                      {getForumChatSubtitle(forum, undefined, session.user?.fullName)}
                    </Typography>
                  </DashboardRowText>
                  <ArrowForwardRoundedIcon color="action" fontSize="small" />
                </DashboardRowLink>
              );
            })}
          </Stack>
          {additionalConversationCount > 0 && (
            <Button component={RouterLink} to={communicationsRoute} size="small">
              +{additionalConversationCount} more {additionalConversationCount === 1 ? "conversation" : "conversations"}
            </Button>
          )}
        </DashboardRowList>
      )}
    </DashboardPanelCard>
  );
}
